import { useEffect, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useSellPassPack } from "@/api/hooks/useMemberships";
import { ContactPicker } from "./ContactPicker";

interface SellPassPackDialogProps {
  open: boolean;
  onClose: () => void;
  onSold?: () => void;
}

/** Sells a $250 Hummingbird Pass Pack (10 day passes, expires 3 months
 * from purchase). Redeeming individual passes from the pack happens
 * later, from the member/contact's day-pass history. */
export function SellPassPackDialog({ open, onClose, onSold }: SellPassPackDialogProps) {
  const [contactId, setContactId] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const sellPassPack = useSellPassPack();

  useEffect(() => {
    if (open) {
      setContactId("");
      setServerError(null);
    }
  }, [open]);

  const canSubmit = !!contactId && !sellPassPack.isPending;

  async function handleSubmit() {
    if (!canSubmit) return;
    setServerError(null);
    try {
      await sellPassPack.mutateAsync(contactId);
      toastSuccess("Pass pack sold");
      onSold?.();
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't sell pass pack", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Sell pass pack">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Sell Hummingbird Pass Pack — $250 (10 passes)</DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Contact</label>
            <ContactPicker value={contactId} onChange={(id) => setContactId(id)} />
          </div>
          <p className="text-xs text-text-muted">Expires 3 months from today.</p>
          {serverError && (
            <p className="text-sm text-danger" role="alert" data-testid="sell-pass-pack-error">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={sellPassPack.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit} data-testid="submit-sell-pass-pack">
            {sellPassPack.isPending ? "Selling…" : "Sell pass pack"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
